import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["searchInput", "dropdown", "tagsContainer"]
  static values = {
    searchUrl: String,
    noResultsText: { type: String, default: "No matching taxonomy terms found" },
    fieldName: { type: String, default: "hsds_service[taxonomy_term_ids][]" }
  }

  declare readonly searchInputTarget: HTMLInputElement
  declare readonly dropdownTarget: HTMLElement
  declare readonly tagsContainerTarget: HTMLElement
  declare searchUrlValue: string
  declare noResultsTextValue: string
  declare fieldNameValue: string

  private searchTimeout: ReturnType<typeof setTimeout> | null = null

  connect() {
    this.handleOutsideClick = this.handleOutsideClick.bind(this)
    document.addEventListener("click", this.handleOutsideClick)
  }

  disconnect() {
    document.removeEventListener("click", this.handleOutsideClick)
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  }

  search() {
    const query = this.searchInputTarget.value.trim()

    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    if (query.length < 2) {
      this.hideDropdown()
      return
    }

    this.searchTimeout = setTimeout(() => {
      this.performSearch(query)
    }, 300)
  }

  async performSearch(query: string) {
    try {
      const url = new URL(this.searchUrlValue, window.location.origin)
      url.searchParams.set("q", query)

      const response = await fetch(url.toString(), {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) return

      const terms = await response.json()
      this.renderResults(terms)
    } catch {
      // Silently handle network errors
    }
  }

  renderResults(terms: Array<{ id: number; name: string; code: string; path: string; color_class?: string }>) {
    const selectedIds = this.getSelectedTermIds()
    const availableTerms = terms.filter(t => !selectedIds.includes(t.id))

    if (availableTerms.length === 0) {
      this.dropdownTarget.innerHTML = `<div class="p-3 text-sm text-base-content/60">${this.noResultsTextValue}</div>`
    } else {
      this.dropdownTarget.innerHTML = availableTerms.map(term => {
        // Events surfaces the calendar-bucket color in the search payload as
        // `color_class`. Other surfaces (Services) omit it and the dot
        // simply doesn't render. Falsy string => no dot.
        const dot = term.color_class
          ? `<span class="inline-block w-2 h-2 rounded-full mr-2 align-middle ${this.escapeHtml(term.color_class)}"></span>`
          : ""
        return `<button type="button"
                 class="w-full text-left px-3 py-2 hover:bg-base-200 text-sm cursor-pointer"
                 data-action="click->taxonomy-tag-manager#selectTerm"
                 data-term-id="${term.id}"
                 data-term-name="${this.escapeHtml(term.name)}"
                 data-term-path="${this.escapeHtml(term.path)}"
                 ${term.color_class ? `data-term-color="${this.escapeHtml(term.color_class)}"` : ""}>
          ${dot}<span class="text-base-content/60">${this.escapeHtml(term.path)}</span>
        </button>`
      }).join("")
    }

    this.showDropdown()
  }

  selectTerm(event: Event) {
    event.preventDefault()
    const button = (event.currentTarget as HTMLElement)
    const termId = button.dataset.termId
    const termName = button.dataset.termName
    const termPath = button.dataset.termPath || ""
    const termColor = button.dataset.termColor || ""

    if (!termId || !termName) return

    this.addTag(parseInt(termId, 10), termName, termPath, termColor)
    this.searchInputTarget.value = ""
    this.hideDropdown()
  }

  addTag(termId: number, termName: string, termPath = "", termColor = "") {
    if (this.getSelectedTermIds().includes(termId)) return

    const tag = document.createElement("span")
    tag.className = "badge badge-outline gap-1"
    tag.dataset.termId = termId.toString()
    if (termPath) tag.dataset.termPath = termPath
    if (termColor) tag.dataset.termColor = termColor

    // If the term sits under a parent, render the breadcrumb in muted text
    // ahead of the leaf name so two leaves of the same name (e.g. "Hot Meals"
    // under "Food" vs under "Workforce") stay distinguishable post-selection.
    const parent = this.parentSegment(termPath, termName)
    const parentMarkup = parent
      ? `<span class="text-base-content/60 mr-1">${this.escapeHtml(parent)} ›</span>`
      : ""

    // Calendar-bucket color dot — events only; absent on Services and
    // other surfaces.
    const dotMarkup = termColor
      ? `<span class="inline-block w-2 h-2 rounded-full ${this.escapeHtml(termColor)}" aria-hidden="true"></span>`
      : ""

    tag.innerHTML = `
      ${dotMarkup}${parentMarkup}${this.escapeHtml(termName)}
      <button type="button" class="ml-1" data-action="click->taxonomy-tag-manager#removeTag" aria-label="Remove ${this.escapeHtml(termName)}">
        <svg class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
      <input type="hidden" name="${this.escapeHtml(this.fieldNameValue)}" value="${termId}" />
    `

    this.tagsContainerTarget.appendChild(tag)
  }

  // Best-effort parent label from a HSDS-style `path` ("Food > Hot Meals").
  // Returns null when the term is top-level or when we can't confidently
  // separate the parent (no separator, or leaf doesn't match the trailing
  // segment — we'd rather show no parent than the wrong one).
  private parentSegment(path: string, leafName: string): string | null {
    if (!path) return null
    const tokens = path.split(/\s*>\s*|\s*\/\s*/).map(t => t.trim()).filter(Boolean)
    if (tokens.length < 2) return null
    const leaf = tokens[tokens.length - 1]
    if (leaf !== leafName) return null
    return tokens.slice(0, -1).join(" › ")
  }

  removeTag(event: Event) {
    event.preventDefault()
    const button = event.currentTarget as HTMLElement
    const tag = button.closest("[data-term-id]")
    if (tag) {
      tag.remove()
    }
  }

  private getSelectedTermIds(): number[] {
    const inputs = this.tagsContainerTarget.querySelectorAll<HTMLInputElement>(
      `input[name='${this.fieldNameValue.replace(/'/g, "\\'")}']`
    )
    return Array.from(inputs).map(input => parseInt(input.value, 10))
  }

  private showDropdown() {
    this.dropdownTarget.classList.remove("hidden")
  }

  private hideDropdown() {
    this.dropdownTarget.classList.add("hidden")
  }

  private handleOutsideClick(event: Event) {
    if (!this.element.contains(event.target as Node)) {
      this.hideDropdown()
    }
  }

  private escapeHtml(text: string): string {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
